import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { roundAmount2Dig } from "utils/amount";
import { arrayMoveImmutable } from "array-move";
import { useBill } from "hooks/bill";
import useBillStore from "store/bill";
import useCompanyPreferences from "hooks/useCompanyPreferences";
import useAuth from "hooks/useAuth";
import Input from "components/ui/Input";
import FileUploader from "components/ui/FileUploader";
import { Table, THead, TBody, TR, TH, TD } from "components/ui/Table";
import { toast } from "react-toastify";

const ArrowUpIcon = (props) => (
  <svg
    width="800px"
    height="800px"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M6 15L12 9L18 15"
      stroke="#000000"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowDownIcon = (props) => (
  <svg
    width="800px"
    height="800px"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="#000000"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function SetSaleBillBottom({
  totalGross,
  totalTax,
  isIGST = true,
  docType,
  handleSubmitForm,
  isLoading = false,
  isSubmitting = false,
  isEditMode = false,
  onCalculatedValues = () => {},
}) {
  const {
    billHeader,
    handleInputChange,
    beforeTaxDiscounts,
    setBeforeTaxDiscounts,
    updateBeforeTaxDiscount,
    afterTaxDiscounts,
    setAfterTaxDiscounts,
    updateAfterTaxDiscount,
    selectBoxes,
    hasApplicableGst,
  } = useBill();
  const savedAdditionalAmounts = useBillStore((state) => state.savedAdditionalAmounts);
  const { isDocUploaderEnabled } = useCompanyPreferences();
  const { agttp } = useAuth();
  const currencyCode = billHeader.currency_code || "INR";
  const currencyPrefix = currencyCode === "INR" ? "₹" : `${currencyCode} `;
  const formatBillAmount = (amount) =>
    new Intl.NumberFormat(currencyCode === "INR" ? "en-IN" : "en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);

  const docPrefix = useMemo(() => {
    if (docType === "AGENTBILL") return "AGENTBILL-";
    if (docType === "CNDN") return "";
    if (agttp === 1) return "STRADERBILL-";
    return "AGENTBILL-";
  }, [docType, agttp]);

  const docFilename = useMemo(() => {
    if (!billHeader.salebillno) return "";
    return `${docPrefix}${billHeader.salebillno}`;
  }, [docPrefix, billHeader.salebillno]);
  const [discountTab, setDiscountTab] = useState(false);
  const [totalAmountAfterAllDiscounts, setTotalAmountAfterAllDiscounts] =
    useState(0);
  const [tcsAmount, setTcsAmount] = useState(0);
  const initializedRef = useRef(false);

  // Initialize from saved values in edit mode
  useEffect(() => {
    if (savedAdditionalAmounts && !initializedRef.current) {
      initializedRef.current = true;
      setTcsAmount(savedAdditionalAmounts.tcsAmount || 0);
    }
  }, [savedAdditionalAmounts]);

  // Calculate total after-tax amount from afterTaxDiscounts
  const totalAfterTaxAmount = useMemo(() => {
    if (!afterTaxDiscounts || afterTaxDiscounts.length === 0) return 0;
    return afterTaxDiscounts.reduce((sum, d) => {
      const amt = Number(d.amount) || 0;
      return d.is_addition === 1 ? sum + amt : sum - amt;
    }, 0);
  }, [afterTaxDiscounts]);

  const handleDiscountTab = () => {
    if (totalGross > 0) setDiscountTab(true);
  };

  // Unified function to calculate discount amount and remaining amount with proper rounding
  const calculateDiscountAndRemaining = useCallback((discounts, baseAmount, upToIndex = null) => {
    let remainingAmount = baseAmount;
    const results = [];

    const discountsToProcess = upToIndex !== null ? discounts.slice(0, upToIndex + 1) : discounts;

    discountsToProcess.forEach((discount, index) => {
      let discountAmount = 0;
      const pct = Number(discount.percentage) || 0;
      const amt = Number(discount.amount) || 0;

      if (pct > 0) {
        discountAmount = roundAmount2Dig((remainingAmount * pct) / 100);
      } else if (amt > 0) {
        if (discount.is_addition === 1) {
          discountAmount = amt;
        } else {
          discountAmount = Math.min(amt, remainingAmount);
        }
      }

      if (discount.is_addition === 1) {
        remainingAmount = roundAmount2Dig(remainingAmount + discountAmount);
      } else {
        remainingAmount = roundAmount2Dig(remainingAmount - discountAmount);
      }

      results.push({
        index,
        discountAmount: roundAmount2Dig(discountAmount),
        remainingAmount,
        discount
      });
    });

    return {
      finalRemainingAmount: remainingAmount,
      results
    };
  }, []);

  const handleDiscountChange = useCallback((index, field, value) => {
    if (field === "percentage") {
      // Calculate remaining amount after all previous discounts
      const { finalRemainingAmount } = calculateDiscountAndRemaining(
        beforeTaxDiscounts, 
        totalGross, 
        index - 1
      );
      
      // Calculate amount for current percentage with proper rounding
      const percentage = parseFloat(value) || 0;
      const calculatedAmount = roundAmount2Dig((finalRemainingAmount * percentage) / 100);
      
      updateBeforeTaxDiscount(index, {
        percentage: percentage,
        amount: calculatedAmount,
        isPercentage: true,
        value: value,
      });
    } else if (field === "amount") {
      // When amount is changed manually, set percentage to 0
      updateBeforeTaxDiscount(index, {
        amount: parseFloat(value) || 0,
        percentage: 0,
        isPercentage: false,
        value: value,
      });
    }
  }, [totalGross, beforeTaxDiscounts, updateBeforeTaxDiscount, calculateDiscountAndRemaining]);

  const moveDiscount = useCallback(
    (index, direction) => {
      if (direction === "up") {
        if (index === 0) return;
        const newDiscounts = arrayMoveImmutable(
          beforeTaxDiscounts,
          index,
          index - 1
        );
        setBeforeTaxDiscounts(newDiscounts);
      } else if (direction === "down") {
        if (index === beforeTaxDiscounts.length - 1) return;
        const newDiscounts = arrayMoveImmutable(
          beforeTaxDiscounts,
          index,
          index + 1
        );
        setBeforeTaxDiscounts(newDiscounts);
      }
    },
    [beforeTaxDiscounts, setBeforeTaxDiscounts]
  );

  const afterTaxBase = useMemo(() => {
    return roundAmount2Dig(totalAmountAfterAllDiscounts + totalTax + tcsAmount);
  }, [totalAmountAfterAllDiscounts, totalTax, tcsAmount]);

  const handleAfterTaxChange = useCallback((index, field, value) => {
    if (field === "percentage") {
      const percentage = parseFloat(value) || 0;
      const calculatedAmount = roundAmount2Dig((afterTaxBase * percentage) / 100);
      updateAfterTaxDiscount(index, {
        percentage,
        amount: calculatedAmount,
      });
    } else {
      updateAfterTaxDiscount(index, {
        amount: parseFloat(value) || 0,
        percentage: 0,
      });
    }
  }, [updateAfterTaxDiscount, afterTaxBase]);

  // Calculate total amount after profiling discounts using unified function
  useEffect(() => {
    if (beforeTaxDiscounts && beforeTaxDiscounts.length > 0) {
      const { finalRemainingAmount } = calculateDiscountAndRemaining(beforeTaxDiscounts, totalGross);
      setTotalAmountAfterAllDiscounts(finalRemainingAmount);
    } else {
      // No discounts, total amount equals gross amount
      setTotalAmountAfterAllDiscounts(totalGross);
    }
  }, [beforeTaxDiscounts, totalGross, calculateDiscountAndRemaining]);

  // Recalculate percentage-based discount amounts when base values change
  useEffect(() => {
    if (beforeTaxDiscounts && beforeTaxDiscounts.length > 0 && totalGross > 0) {
      let hasChanges = false;
      const updatedDiscounts = [...beforeTaxDiscounts];
      const { results } = calculateDiscountAndRemaining(beforeTaxDiscounts, totalGross);
      
      results.forEach((result, i) => {
        const discount = updatedDiscounts[i];
        if (discount.percentage > 0 && result.discountAmount !== discount.amount) {
          updatedDiscounts[i] = { ...discount, amount: result.discountAmount };
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setBeforeTaxDiscounts(updatedDiscounts);
      }
    }

    if (afterTaxDiscounts && afterTaxDiscounts.length > 0 && afterTaxBase > 0) {
      let hasChanges = false;
      const updated = afterTaxDiscounts.map((d) => {
        if (d.percentage > 0) {
          const newAmount = roundAmount2Dig((afterTaxBase * d.percentage) / 100);
          if (newAmount !== d.amount) {
            hasChanges = true;
            return { ...d, amount: newAmount };
          }
        }
        return d;
      });
      if (hasChanges) {
        setAfterTaxDiscounts(updated);
      }
    }
  }, [totalGross, beforeTaxDiscounts, setBeforeTaxDiscounts, calculateDiscountAndRemaining, afterTaxBase, afterTaxDiscounts, setAfterTaxDiscounts]);

  // Calculate TCS (Tax Collected at Source)
  // TCS is calculated on: sum_after_discounts + taxes
  // Formula: tcsamt = (tcsperc / 100) * (totalAmountAfterAllDiscounts + totalTax)
  useEffect(() => {
    const tcsPerc = selectBoxes.vendor?.tcsperc || 0;
    if (tcsPerc > 0) {
      const sumForTcs = totalAmountAfterAllDiscounts + totalTax;
      const calculatedTcs = Math.round((tcsPerc / 100) * sumForTcs * 100) / 100;
      setTcsAmount(calculatedTcs);
    } else {
      setTcsAmount(0);
    }
  }, [totalAmountAfterAllDiscounts, totalTax, selectBoxes.vendor]);

  // Handler for TCS amount change
  // Only allows 0 or calculated amount (matching PHP behavior)
  const handleTcsChange = useCallback((e) => {
    const value = parseFloat(e.target.value) || 0;
    
    if (value > 0) {
      toast.warning("Only Calculated amount or zero is allowed in TCS");
      // Reset to calculated amount
      const tcsPerc = selectBoxes.vendor?.tcsperc || 0;
      const sumForTcs = totalAmountAfterAllDiscounts + totalTax;
      const calculatedTcs = Math.round((tcsPerc / 100) * sumForTcs * 100) / 100;
      setTcsAmount(calculatedTcs);
    } else {
      setTcsAmount(0);
    }
  }, [selectBoxes.vendor, totalAmountAfterAllDiscounts, totalTax]);

  // Pass calculated values to parent component
  useEffect(() => {
    onCalculatedValues({
      totalAmountAfterAllDiscounts,
      freightAfterTax: totalAfterTaxAmount,
      tcsAmount,
    });
  }, [totalAmountAfterAllDiscounts, totalAfterTaxAmount, tcsAmount, onCalculatedValues]);

  let stgst = 0,
    cgst = 0,
    igst = 0;
  if (isIGST) {
    igst = roundAmount2Dig(totalTax);
  } else {
    cgst = roundAmount2Dig(totalTax / 2);
    stgst = roundAmount2Dig(totalTax / 2);
  }

  // Get TCS percentage from vendor
  const tcsPerc = selectBoxes.vendor?.tcsperc || 0;

  return (
    <div>
      <div className="w-full border-t border-gray-300" />
      <div className="flex flex-col sm:flex-row gap-3 pt-4">
        <div className="w-full sm:w-1/4">
          {["AGENTBILL", "CNDN"].includes(docType) ? (
            <button
              type="button"
              className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700"
              onClick={handleDiscountTab}
            >
              Proceed to tax calculation
            </button>
          ) : null}

          <div className="mt-4">
            <label
              htmlFor="comment"
              className="block text-sm font-medium leading-6 text-gray-900"
            >
              Remarks
            </label>
            <div className="mt-2">
              <Input
                id="comment"
                name="comment"
                type="textarea"
                rows={2}
                value={billHeader.remark || ""}
                onChange={(e) => {
                  handleInputChange("remark", e.target.value);
                }}
                autoComplete="lrno"
              />
            </div>
          </div>
          {isDocUploaderEnabled() && docFilename && (
            <FileUploader docFilename={docFilename} />
          )}
          {isEditMode && billHeader.currency_code !== "INR" && (
            <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              <p className="font-medium">FX reference (read-only)</p>
              <p>
                {billHeader.currency_code}/INR: {billHeader.fx_rate_to_inr || "—"}
              </p>
              {(billHeader.fx_rate_date || billHeader.fx_rate_source) && (
                <p className="text-xs text-gray-500">
                  {[billHeader.fx_rate_date, billHeader.fx_rate_source]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
            </div>
          )}
        </div>
        {["AGENTBILL", "CNDN"].includes(docType) && discountTab ? (
          <div className="w-full sm:w-3/4">
            <div className="flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                    <Table>
                      <THead>
                        <TR>
                          <TH first>Discount</TH>
                          <TH>Discount %</TH>
                          <TH last>Discount {currencyCode}</TH>
                        </TR>
                      </THead>
                      <TBody>
                        {beforeTaxDiscounts && beforeTaxDiscounts.length
                          ? beforeTaxDiscounts.map((x, xi) => {
                              return (
                                <TR key={`discount-${xi}`}>
                                  <TD first className="flex gap-2 items-center">
                                    <div className="flex flex-col">
                                      <ArrowUpIcon
                                        onClick={() => moveDiscount(xi, "up")}
                                        className="cursor-pointer h-3 w-3"
                                      />
                                      <ArrowDownIcon
                                        onClick={() => moveDiscount(xi, "down")}
                                        className="cursor-pointer h-3 w-3"
                                      />
                                    </div>
                                    <span>{x.name}</span>
                                  </TD>
                                  <TD>
                                    <Input
                                      type="number"
                                      value={x.percentage}
                                      onChange={(e) =>
                                        handleDiscountChange(
                                          xi,
                                          "percentage",
                                          e.target.value
                                        )
                                      }
                                      className="text-right w-full"
                                    />
                                  </TD>
                                  <TD last>
                                    <Input
                                      type="number"
                                      value={x.amount}
                                      onChange={(e) =>
                                        handleDiscountChange(
                                          xi,
                                          "amount",
                                          e.target.value
                                        )
                                      }
                                      className="text-right w-full"
                                    />
                                  </TD>
                                </TR>
                              );
                            })
                          : null}

                        <TR>
                          <TD first>Total Amount after all discounts:</TD>
                          <TD></TD>
                          <TD last className="text-right">
                            {currencyPrefix}
                            {formatBillAmount(totalAmountAfterAllDiscounts)}
                          </TD>
                        </TR>
                        {hasApplicableGst && isIGST ? (
                          <TR>
                            <TD first>IGST:</TD>
                            <TD></TD>
                            <TD last className="text-right">
                              {currencyPrefix}{formatBillAmount(igst)}
                            </TD>
                          </TR>
                        ) : hasApplicableGst ? (
                          <>
                            <TR>
                              <TD first>SGST:</TD>
                              <TD></TD>
                              <TD last className="text-right">
                                {currencyPrefix}{formatBillAmount(stgst)}
                              </TD>
                            </TR>
                            <TR>
                              <TD first>CGST:</TD>
                              <TD></TD>
                              <TD last className="text-right">
                                {currencyPrefix}{formatBillAmount(cgst)}
                              </TD>
                            </TR>
                          </>
                        ) : null}

                        <TR>
                          <TD first>TCS ({tcsPerc}%):</TD>
                          <TD></TD>
                          <TD last>
                            <Input
                              type="number"
                              step="0.01"
                              value={tcsAmount}
                              onChange={handleTcsChange}
                              className="text-right w-full"
                            />
                          </TD>
                        </TR>

                        {afterTaxDiscounts && afterTaxDiscounts.length > 0 &&
                          afterTaxDiscounts.map((atd, ati) => (
                            <TR key={`after-tax-${ati}`}>
                              <TD first>
                                {atd.is_addition === 1 ? "Add" : "Less"}: {atd.name}
                              </TD>
                              <TD>
                                <Input
                                  type="number"
                                  value={atd.percentage}
                                  onChange={(e) => handleAfterTaxChange(ati, "percentage", e.target.value)}
                                  className="text-right w-full"
                                />
                              </TD>
                              <TD last>
                                <Input
                                  type="number"
                                  value={atd.amount}
                                  onChange={(e) => handleAfterTaxChange(ati, "amount", e.target.value)}
                                  className="text-right w-full"
                                />
                              </TD>
                            </TR>
                          ))
                        }

                        <TR>
                          <TD first>{docType === "CNDN" ? "Note Value:" : "Salebill Value:"}</TD>
                          <TD></TD>
                          <TD last className="text-right">
                            {currencyPrefix}
                            {formatBillAmount(
                              totalAmountAfterAllDiscounts +
                                totalTax +
                                tcsAmount +
                                totalAfterTaxAmount
                            )}
                          </TD>
                        </TR>
                      </TBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="mt-4 flex flex-wrap justify-between gap-2">
        <button
          type="button"
          className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700"
        >
          Main Page
        </button>
        <button
          type="button"
          onClick={() => handleSubmitForm()}
          disabled={isSubmitting}
          className={`rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
            isSubmitting
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-gray-700 hover:bg-gray-500 focus-visible:outline-gray-700"
          }`}
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {isEditMode ? "Updating..." : "Saving..."}
            </div>
          ) : (
            isEditMode ? "Update" : "Save"
          )}
        </button>
      </div>
    </div>
  );
}
